import { useCallback, type Ref } from 'react';
import { useIsProjectDemo } from '@/features/projectDemo';
import { Link, useLocation, matchPath, generatePath } from 'react-router-dom';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { Rating, type BoundingBoxString } from '@/features/types';
import type { GroupAnalysisData } from '@/generated/types/navigator/types.gen';
import {
  useClientTerminology,
  type ClientTerminology,
} from '@/views/list/NetworkTerms';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setListView } from '@/features/analysisMapSlice';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';

import {
  getCategoryIndex,
  getCategoryThresholds,
  getCategoryTitle,
  getNetworkColors,
  getSpanCategories,
  getSpanNetworkColors,
} from '@/views/map/Legend/utils';
import { Flex, Tooltip, useMantineTheme } from '@mantine/core';
import { Level3WorkStatus } from './field/Level3WorkStatus';
import { EncroachmentRiskRating } from './field/EncroachmentRiskRating';
import { ScanTagList } from '@/components/Tag/ScanTagList';
import { capitalize } from '@/helpers/stringConversion';
import { PriorityRating } from './field/PriorityRating';
import { FuelHazardRating } from './field/FuelHazardRating';
import { RiskTag, type RiskTagTier } from '@/components/RiskTag/RiskTag';
import { useUserClientContext } from '@/app/clientContext';
import { isPge } from '@/features/utils';
import classes from './GroupItem.module.css';
import { isInProjectUrl } from '@/helpers/url';
import { useHasGroupRiskSummary } from '@/hooks/useWildfireMode';
import { useHasWildfirePriority } from '@/hooks/useHasWildfirePriority';
import { formatWildfireCurrency } from '@/helpers/wildfire';
import { getRatingColor } from '@/helpers/encroachmentRating';
import { useCurrentRootPath } from '@/views/useUrlSync';

import CustomListScroller from './custom-lists/CustomListScroller';
import { LISTS_DETAIL_PATH, LISTS_SPAN_PATH } from '@/app/constants';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { useHover } from '@/features/hoverContext';

type GroupItemProps = {
  a: GroupAnalysisData;
  onClick?: (bbox: BoundingBoxString | undefined, group?: string) => void;
  ref?: Ref<HTMLDivElement>;
};

const getTotal = (
  analysisType: GroupAnalysisType,
  a: GroupAnalysisData,
  hasGroupRiskSummary: boolean
) => {
  switch (analysisType) {
    case GroupAnalysisType.ENCROACHMENT:
      // Total encroachment score can be 0, negative scores are unknown
      return a.totalEncScore != null && a.totalEncScore >= 0
        ? a.totalEncScore
        : null;
    case GroupAnalysisType.PRIORITY:
      // Total priority score of 0 is unknown
      return a.totalPriorityScore ? a.totalPriorityScore : null;
    case GroupAnalysisType.WILDFIRE_PRIORITY:
      if (hasGroupRiskSummary) {
        return a.wildfireRisk ?? null;
      }
      // Total wildfire priority score of 0 is unknown
      return a.totalWildfirePriorityScore ? a.totalWildfirePriorityScore : null;
    case GroupAnalysisType.WORKLOAD_ANALYSIS:
      return a.criticalTreesCount !== undefined ? a.criticalTreesCount : null;
    case GroupAnalysisType.CLEAR:
      return a.clearPercentage !== undefined ? a.clearPercentage : null;
    case GroupAnalysisType.FALL_IN:
      return a.hazardTreesCount !== undefined ? a.hazardTreesCount : null;
  }
};

const formatPhasing = (phasing: string): string => {
  if (!phasing) {
    return phasing;
  }

  return capitalize(phasing).replace('-', ' ');
};

const formatVoltage = (
  intl: ClientTerminology,
  voltage: number | undefined
): string => {
  if (voltage === undefined || voltage === -1) return 'N/A';

  return `${intl.formatNumber(voltage, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} kV`;
};

export const GroupItem = (props: GroupItemProps) => {
  const { a, onClick, ref } = props;
  const dispatch = useAppDispatch();
  const isSmallScreen = useSmallScreen();
  const theme = useMantineTheme();
  const intl = useClientTerminology();
  const navigateWithSearch = useNavigateWithSearch();
  const rootPath = useCurrentRootPath();
  const { formatMessage } = useClientTerminology();
  const { context } = useUserClientContext();
  const location = useLocation();
  const { setHoveredItem } = useHover();

  // Extract the list ID from the current URL if we're in a custom list context
  const listMatch =
    matchPath(LISTS_DETAIL_PATH, location.pathname) ??
    matchPath(LISTS_SPAN_PATH, location.pathname);
  const currentListId = listMatch?.params?.id;

  const {
    selectedAnalysis,
    selectedNetworkGroup,
    encroachmentThresholds,
    priorityThresholds,
    wildfirePriorityThresholds,
    criticalTreesThresholds,
    clearThresholds,
    activeGroupName,
    spanNetworkGroup,
    currentViewMode,
  } = useAppSelector((s) => s.analysisMap);
  const isProjectDemo = useIsProjectDemo();

  const isGroupingBySpan =
    selectedNetworkGroup?.id === spanNetworkGroup?.id || activeGroupName;

  const hasGroupRiskSummary = useHasGroupRiskSummary();
  const hasWildfirePriority = useHasWildfirePriority();

  const handleMouseEnter = useCallback(() => {
    setHoveredItem({
      bbox: a.bbox as BoundingBoxString,
      groupName: isGroupingBySpan ? undefined : (a.group ?? undefined),
      source: 'list',
    });
  }, [a.bbox, a.group, isGroupingBySpan, setHoveredItem]);

  const handleMouseLeave = useCallback(() => {
    setHoveredItem(null);
  }, [setHoveredItem]);

  if (!selectedNetworkGroup) return null;

  const total = getTotal(selectedAnalysis, a, hasGroupRiskSummary);
  const isEmptyGroup = !a.group;
  const hidesWildfirePriority =
    selectedAnalysis === GroupAnalysisType.WILDFIRE_PRIORITY &&
    !hasWildfirePriority;

  const isWildfireDollars =
    selectedAnalysis === GroupAnalysisType.WILDFIRE_PRIORITY &&
    hasGroupRiskSummary;
  const numDigits = selectedAnalysis === GroupAnalysisType.ENCROACHMENT ? 1 : 0;
  const displayTotal =
    total !== null && total !== undefined
      ? isWildfireDollars
        ? formatWildfireCurrency(total)
        : intl.formatNumber(total, {
            minimumFractionDigits: numDigits,
            maximumFractionDigits: numDigits,
            useGrouping: false,
          })
      : null;

  const groupCategoryThresholds = getCategoryThresholds(
    selectedAnalysis,
    encroachmentThresholds,
    priorityThresholds,
    wildfirePriorityThresholds,
    criticalTreesThresholds,
    clearThresholds
  );

  const spanCategories = getSpanCategories(
    selectedAnalysis,
    criticalTreesThresholds
  );

  const categoryThresholds = isGroupingBySpan
    ? spanCategories
    : groupCategoryThresholds;

  const categoryIndex =
    total !== null && categoryThresholds
      ? getCategoryIndex(categoryThresholds, Number(total.toFixed(1)))
      : null;

  const networkColors = isGroupingBySpan
    ? getSpanNetworkColors(selectedAnalysis, theme)
    : getNetworkColors(selectedAnalysis, theme);

  const indicatorColor =
    categoryIndex === undefined || categoryIndex === null
      ? theme.other.riskColor.unknown
      : (networkColors && networkColors[categoryIndex]) ||
        theme.other.riskColor.unknown;

  const length =
    a.length &&
    a.metric &&
    intl.formatNumber(a.length, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
      unit: a.metric,
      unitDisplay: 'short',
      style: 'unit',
    });

  const groupItem = (
    <div className={classes.container}>
      <div className={classes.header}>
        <Tooltip label={a.displayName ?? a.group} withArrow openDelay={500}>
          <span className={classes.group}>
            {a.displayName ??
              a.group ??
              `No ${selectedNetworkGroup.singularInline}`}
          </span>
        </Tooltip>
      </div>
      <div className={classes.body}>
        {!isEmptyGroup && !hidesWildfirePriority && (
          <div className={classes.section}>
            {getCategoryTitle(
              selectedAnalysis,
              formatMessage,
              false,
              hasGroupRiskSummary
            )}
            <div className={classes.bodyItem}>
              {selectedAnalysis === GroupAnalysisType.PRIORITY ||
              selectedAnalysis === GroupAnalysisType.WILDFIRE_PRIORITY ? (
                <RiskTag
                  tier={
                    total === null
                      ? 'unknown'
                      : (Math.min(categoryIndex ?? 0, 2) as RiskTagTier)
                  }
                  data-testid="risk-tag"
                >
                  {total !== null
                    ? displayTotal
                    : intl.formatMessage({
                        id: 'level3.unknownRatingLabel',
                      })}
                </RiskTag>
              ) : selectedAnalysis !== GroupAnalysisType.CLEAR ? (
                <>
                  {selectedAnalysis !== GroupAnalysisType.FALL_IN && (
                    <span
                      className={classes.indicator}
                      style={{
                        backgroundColor: indicatorColor,
                        borderColor: indicatorColor,
                      }}
                    />
                  )}
                  <span className={classes.total}>
                    {total !== null
                      ? displayTotal
                      : intl.formatMessage({
                          id: 'level3.unknownRatingLabel',
                        })}
                  </span>
                </>
              ) : a.clearPercentage !== null ? (
                `${a.clearPercentage?.toFixed()}% (${
                  a.clearSpanCount
                } of ${a.totalSpans})`
              ) : (
                0
              )}
            </div>
          </div>
        )}
        {selectedAnalysis === GroupAnalysisType.WILDFIRE_PRIORITY &&
          a.fuelHazardRating != null &&
          a.fuelHazardScore != null && (
            <div className={classes.section}>
              {formatMessage({ id: 'list.fuelHazardScore' })}
              <div className={classes.bodyItem}>
                <span
                  className={classes.indicator}
                  style={{
                    backgroundColor: getRatingColor(a.fuelHazardRating, theme),
                  }}
                  data-testid="fuel-hazard-indicator"
                />
                <span className={classes.total}>
                  {intl.formatNumber(a.fuelHazardScore, {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  })}
                </span>
              </div>
            </div>
          )}
        <div className={classes.section}>{length} total</div>
      </div>
    </div>
  );

  // Span level components

  const groupEl = (
    <Tooltip label={a.displayName ?? a.group} withArrow openDelay={500}>
      <span className={classes.group}>{a.displayName ?? a.group}</span>
    </Tooltip>
  );

  const phasingOrVoltage = (
    <>
      {a.phasingType && a.network === 1 && (
        <div className={classes.bodyItem}>{formatPhasing(a.phasingType)}</div>
      )}

      {a.voltageInKv && a.network === 2 && (
        <div className={classes.bodyItem}>
          {formatVoltage(intl, a.voltageInKv)}
        </div>
      )}
    </>
  );

  const workStatus = a.workStatus && (
    <Level3WorkStatus workStatus={a.workStatus} />
  );

  const tags = a.tagsList && (
    <div>
      <ScanTagList tags={a.tagsList} />
    </div>
  );

  const encSpanItem = (
    <div className={classes.container}>
      <div className={classes.header}>
        {groupEl}
        <div className={classes.workStatusWrapper}>
          {workStatus}
          <EncroachmentRiskRating
            rating={a.encRating ?? Rating.Unknown}
            score={a.totalEncScore ?? -1}
          />
        </div>
      </div>
      <div className={classes.body}>
        <div className={classes.section}>{phasingOrVoltage}</div>
        {tags}
      </div>
    </div>
  );

  const prioritySpanItem = (
    <div className={classes.container}>
      <div className={classes.header}>
        {groupEl}
        <div className={classes.workStatusWrapper}>
          {workStatus}
          {a.priorityRating != null && !isPge(context) && (
            <PriorityRating priorityRating={a.priorityRating} />
          )}
        </div>
      </div>
      <div className={classes.body}>
        <div className={classes.section}>
          <Flex gap={4}>
            <span>Enc. level:</span>
            <EncroachmentRiskRating
              rating={a.encRating ?? Rating.Unknown}
              score={a.totalEncScore ?? -1}
              variant="secondary"
            />
          </Flex>
          {phasingOrVoltage}
        </div>
        {tags}
      </div>
    </div>
  );

  const wildfirePrioritySpanItem = (
    <div className={classes.container}>
      <div className={classes.header}>
        {groupEl}
        <div className={classes.workStatusWrapper}>
          {workStatus}
          {a.wildfirePriorityRating != null &&
            hasWildfirePriority &&
            !isPge(context) && (
              <PriorityRating priorityRating={a.wildfirePriorityRating} />
            )}
        </div>
      </div>
      <div className={classes.body}>
        {a.fuelHazardRating != null && (
          <div className={classes.section}>
            <Flex gap={4}>
              <span>{formatMessage({ id: 'level3.fuelHazard.title' })}:</span>
              <FuelHazardRating
                rating={a.fuelHazardRating}
                score={a.fuelHazardScore}
                variant="secondary"
              />
            </Flex>
          </div>
        )}
        {tags}
      </div>
    </div>
  );

  const clearSpanItem = (
    <div className={classes.container}>
      <div className={classes.header}>
        {groupEl}
        <div className={classes.workStatusWrapper}>{workStatus}</div>
      </div>
      <div className={classes.body}>
        <div className={classes.section}>
          <Flex gap={4}>
            <span>Enc. level:</span>
            <EncroachmentRiskRating
              rating={a.encRating ?? Rating.Unknown}
              score={a.totalEncScore ?? -1}
              variant="secondary"
            />
          </Flex>
          <div className={classes.bodyItem}>Strike trees: 0</div>
        </div>
        <div className={classes.section}>{phasingOrVoltage}</div>
        {tags}
      </div>
    </div>
  );

  const workloadAnalysisSpanItem = (
    <div className={classes.container}>
      <div className={classes.header}>
        {groupEl}
        <div className={classes.countWrapper}>
          {workStatus}
          <span>Critical trees:</span>
          <span
            className={classes.indicator}
            style={{
              backgroundColor: indicatorColor,
              borderColor: indicatorColor,
            }}
          />
          {displayTotal}
        </div>
      </div>
      <div className={classes.body}>
        <div className={classes.section}>{phasingOrVoltage}</div>
        {tags}
      </div>
    </div>
  );

  const fallInAnalysisSpanItem = (
    <div className={classes.container}>
      <div className={classes.header}>
        {groupEl}
        <div className={classes.countWrapper}>
          {workStatus}
          <span>
            {formatMessage({ id: 'list.fallInHazardTreesCountLabel' })}:{' '}
          </span>
          {displayTotal}
        </div>
      </div>
      <div className={classes.body}>
        <div className={classes.section}>{phasingOrVoltage}</div>
        {tags}
      </div>
    </div>
  );

  const getSpanItem = (analysisType: GroupAnalysisType) => {
    switch (analysisType) {
      case GroupAnalysisType.ENCROACHMENT:
        return encSpanItem;
      case GroupAnalysisType.PRIORITY:
        return prioritySpanItem;
      case GroupAnalysisType.WILDFIRE_PRIORITY:
        return wildfirePrioritySpanItem;
      case GroupAnalysisType.WORKLOAD_ANALYSIS:
        return workloadAnalysisSpanItem;
      case GroupAnalysisType.CLEAR:
        return clearSpanItem;
      case GroupAnalysisType.FALL_IN:
        return fallInAnalysisSpanItem;
    }
  };

  if (isInProjectUrl(currentViewMode) && currentListId) {
    return (
      <div
        key={a.group}
        ref={ref}
        className={classes.visualItemWrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Link
          to={{
            pathname: generatePath(LISTS_SPAN_PATH, {
              id: currentListId,
              // we base64 encode the level2 and level3 to create a unique identifier
              // which allows us to reliably split on the '~' character and decode it later
              spanId: `${btoa(a.level2 ?? '')}~${btoa(a.group ?? '')}`,
            }),
            search: location.search,
          }}
          onClick={() => {
            // On mobile, switch to map view so the span detail drawer
            // opens over the map instead of overlaying the list.
            if (isSmallScreen) {
              dispatch(setListView(false));
            }
            TagManager.dataLayer({
              dataLayer: {
                event: GoogleEvents.projects.engagement.spanClickMapList, // clicked on span link in project sidebar
              },
            });
          }}
        >
          {getSpanItem(selectedAnalysis)}
        </Link>
        {!isProjectDemo && <CustomListScroller a={a} />}
      </div>
    );
  }

  return isGroupingBySpan || activeGroupName ? (
    <div
      key={a.group}
      ref={ref}
      className={classes.visualItemWrapper}
      data-testid="group-item"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link
        key={a.group}
        to={{
          pathname: `/group/${a.group && encodeURIComponent(a.group)}`,
          search: location.search,
        }}
        onClick={() => {
          // On mobile, switch to map view so the span detail drawer
          // opens over the map instead of overlaying the list.
          // This also handles re-selecting the same span: React Router
          // won't re-navigate to the same URL, so the useEffect in
          // useSelectedSpanSync won't re-fire. The explicit dispatch
          // ensures the view always switches.
          if (isSmallScreen) {
            dispatch(setListView(false));
          }
        }}
      >
        {getSpanItem(selectedAnalysis)}
      </Link>
      {!isProjectDemo && <CustomListScroller a={a} />}
    </div>
  ) : (
    <Flex
      direction="column"
      key={a.group}
      ref={ref}
      className={classes.visualItemWrapper}
      data-testid="group-item"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => {
        onClick?.(a.bbox as BoundingBoxString, a.group ?? undefined);
        navigateWithSearch(rootPath);
      }}
    >
      {groupItem}
    </Flex>
  );
};
